import {useMessage} from '@coolcolduk/ui-mui';
import {ExpandMore} from '@mui/icons-material';
import {Accordion, AccordionDetails, AccordionSummary, Button, Stack, TextField, Typography} from '@mui/material';
import {useCallback, useState} from 'react';
import {ExamModule, ModuleTopic} from '@cp/model';
import GenerateStudyPromptDialog from '../../../components/PromptDialogs/GenerateStudyPromptDialog';
import {useGenerateStudyPrompts} from '../../../hooks/generate/useGenerateStudyPrompts';
import {useGetPdfExtractQuestionPrompt} from '../../../hooks/generate/useGetPdfExtractQuestionSystemPrompt';
import {useGetPdfSystemPrompt} from '../../../hooks/generate/useGetPdfSystemPrompt';
import AdminStudyQuestionsImport from './AdminStudyQuestionsImport';

export interface AdminStudyGenerationProps {
  selectedModule?: ExamModule;
  selectedTopic?: ModuleTopic;
  examModules: ExamModule[];
  modulesLoading: boolean;

  additionalInfo: string;
  onAdditionalInfoChange: (info: string) => void;
  onContentChange: (content: string) => void;

  onGenerate: () => void;

  generating: boolean;
  saving: boolean;
  studyLoading: boolean;
}

export default function AdminStudyGeneration({
  selectedModule,
  selectedTopic,
  examModules,
  modulesLoading,
  additionalInfo,
  onAdditionalInfoChange,
  onContentChange,
  onGenerate,
  generating,
  saving,
  studyLoading,
}: AdminStudyGenerationProps) {
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const {showMessage} = useMessage();
  const {generateStudyPrompts, loading: promptsLoading} = useGenerateStudyPrompts();
  const {getPdfSystemPrompt, loading: pdfPromptLoading} = useGetPdfSystemPrompt();
  const {getPdfExtractQuestionPrompt, loading: pdfExtractQuestionPromptLoading} = useGetPdfExtractQuestionPrompt();

  const handleApplyStudyContent = (
    studyContent: string,
    _moduleId: string,
    _topic: string,
    additionalInfoFromDialog?: string,
  ) => {
    onContentChange(studyContent);
    if (additionalInfoFromDialog) {
      onAdditionalInfoChange(additionalInfoFromDialog);
    }
  };

  const handleCopyUserPrompt = useCallback(async () => {
    if (!selectedModule || !selectedTopic) {
      showMessage('Please select a module and topic', 6000, 'error');
      return;
    }

    try {
      const prompts = await generateStudyPrompts({
        moduleId: selectedModule.id,
        topic: selectedTopic.topic,
        additionalInfo: additionalInfo.trim() || undefined,
      });

      await navigator.clipboard.writeText(prompts.user);
      showMessage('User prompt copied to clipboard', 3000, 'success');
    } catch (error) {
      console.error('Error copying user prompt:', error);
      showMessage('Failed to copy user prompt. Please try again.', 6000, 'error');
    }
  }, [selectedModule, selectedTopic, additionalInfo, generateStudyPrompts, showMessage]);

  const handleCopySystemPrompt = useCallback(async () => {
    if (!selectedModule || !selectedTopic) {
      showMessage('Please select a module and topic', 6000, 'error');
      return;
    }

    try {
      const prompts = await generateStudyPrompts({
        moduleId: selectedModule.id,
        topic: selectedTopic.topic,
        additionalInfo: additionalInfo.trim() || undefined,
      });

      await navigator.clipboard.writeText(prompts.system);
      showMessage('System prompt copied to clipboard', 3000, 'success');
    } catch (error) {
      console.error('Error copying system prompt:', error);
      showMessage('Failed to copy system prompt. Please try again.', 6000, 'error');
    }
  }, [selectedModule, selectedTopic, additionalInfo, generateStudyPrompts, showMessage]);

  const handleCopyPdfSystemPrompt = useCallback(async () => {
    try {
      const prompts = await getPdfSystemPrompt(additionalInfo.trim() || undefined);
      await navigator.clipboard.writeText(prompts.system);
      showMessage('PDF system prompt copied to clipboard', 3000, 'success');
    } catch (error) {
      console.error('Error copying PDF system prompt:', error);
      showMessage('Failed to copy PDF system prompt. Please try again.', 6000, 'error');
    }
  }, [getPdfSystemPrompt, additionalInfo, showMessage]);

  const handleCopyPdfUserPrompt = useCallback(async () => {
    try {
      const prompts = await getPdfSystemPrompt(additionalInfo.trim() || undefined);
      await navigator.clipboard.writeText(prompts.user);
      showMessage('PDF user prompt copied to clipboard', 3000, 'success');
    } catch (error) {
      console.error('Error copying PDF user prompt:', error);
      showMessage('Failed to copy PDF user prompt. Please try again.', 6000, 'error');
    }
  }, [getPdfSystemPrompt, additionalInfo, showMessage]);

  const handleCopyPdfExtractQuestionSystemPrompt = useCallback(async () => {
    try {
      const prompts = await getPdfExtractQuestionPrompt(additionalInfo.trim() || undefined);
      await navigator.clipboard.writeText(prompts.system);
      showMessage('PDF extract question system prompt copied to clipboard', 3000, 'success');
    } catch (error) {
      console.error('Error copying PDF extract question system prompt:', error);
      showMessage('Failed to copy PDF extract question system prompt. Please try again.', 6000, 'error');
    }
  }, [getPdfExtractQuestionPrompt, additionalInfo, showMessage]);

  const handleCopyPdfExtractQuestionUserPrompt = useCallback(async () => {
    try {
      const prompts = await getPdfExtractQuestionPrompt(additionalInfo.trim() || undefined);
      await navigator.clipboard.writeText(prompts.user);
      showMessage('PDF extract question user prompt copied to clipboard', 3000, 'success');
    } catch (error) {
      console.error('Error copying PDF extract question user prompt:', error);
      showMessage('Failed to copy PDF extract question user prompt. Please try again.', 6000, 'error');
    }
  }, [getPdfExtractQuestionPrompt, additionalInfo, showMessage]);

  if (!selectedModule || !selectedTopic) {
    return null;
  }

  return (
    <>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" sx={{fontWeight: 600}}>
            Generation
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Information (Optional)"
              placeholder="Add any specific requirements, focus areas, or additional context for the study guide..."
              value={additionalInfo}
              onChange={(e) => onAdditionalInfoChange(e.target.value)}
              helperText="Specify what should be included, emphasized, or any other requirements for the generated study guide"
            />
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Button variant="outlined" onClick={onGenerate} disabled={generating || saving || studyLoading}>
                {generating ? 'Generating...' : 'Generate Study'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowPromptDialog(true)}
                disabled={generating || saving || studyLoading}
              >
                Generate with Prompts
              </Button>
            </Stack>
            <Typography variant="h6" sx={{fontWeight: 600}}>
              Copy Prompts
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Button
                variant="outlined"
                onClick={handleCopyUserPrompt}
                disabled={generating || saving || studyLoading || promptsLoading}
              >
                {promptsLoading ? 'Loading...' : 'uPrompt'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCopySystemPrompt}
                disabled={generating || saving || studyLoading || promptsLoading}
              >
                {promptsLoading ? 'Loading...' : 'sPrompt'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCopyPdfSystemPrompt}
                disabled={generating || saving || studyLoading || pdfPromptLoading}
              >
                {pdfPromptLoading ? 'Loading...' : 'PDF sprompt'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCopyPdfUserPrompt}
                disabled={generating || saving || studyLoading || pdfPromptLoading}
              >
                {pdfPromptLoading ? 'Loading...' : 'PDF uprompt'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCopyPdfExtractQuestionSystemPrompt}
                disabled={generating || saving || studyLoading || pdfExtractQuestionPromptLoading}
              >
                {pdfExtractQuestionPromptLoading ? 'Loading...' : 'PDF extract q sprompt'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCopyPdfExtractQuestionUserPrompt}
                disabled={generating || saving || studyLoading || pdfExtractQuestionPromptLoading}
              >
                {pdfExtractQuestionPromptLoading ? 'Loading...' : 'PDF extract q uprompt'}
              </Button>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <GenerateStudyPromptDialog
        open={showPromptDialog}
        examModules={examModules}
        modulesLoading={modulesLoading}
        initialModuleId={selectedModule?.id}
        initialTopic={selectedTopic?.topic}
        initialAdditionalInfo={additionalInfo}
        onClose={() => setShowPromptDialog(false)}
        onApply={handleApplyStudyContent}
      />

      <AdminStudyQuestionsImport selectedModule={selectedModule} selectedTopic={selectedTopic} />
    </>
  );
}
